import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { 
  Wallet, 
  Building2, 
  TrendingUp, 
  TrendingDown, 
  ArrowUpRight, 
  ArrowDownRight,
  Plus,
  RefreshCw,
  ArrowLeftRight
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BalanceSheetPage() {
  const { language } = useLanguage();
  const [balance, setBalance] = useState(null);
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    adjustment_type: 'deposit',
    account: 'cash',
    from_account: 'cash',
    to_account: 'bank',
    amount: '',
    description: '',
    notes: ''
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [balanceRes, adjustmentsRes] = await Promise.allSettled([
        axios.get(`${API}/balance-sheet`),
        axios.get(`${API}/balance-adjustments`)
      ]);
      if (balanceRes.status === 'fulfilled') setBalance(balanceRes.value.data);
      if (adjustmentsRes.status === 'fulfilled') setAdjustments(adjustmentsRes.value.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/balance-adjustments`, {
        ...formData,
        amount: parseFloat(formData.amount)
      });
      fetchData();
      setDialogOpen(false);
      setFormData({
        adjustment_type: 'deposit',
        account: 'cash',
        from_account: 'cash',
        to_account: 'bank',
        amount: '',
        description: '',
        notes: ''
      });
    } catch (error) {
      console.error('Error creating adjustment:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const t = (key) => {
    const translations = {
      balanceSheet: { en: 'Balance Sheet', ar: 'كشف الحساب' },
      cashBalance: { en: 'Cash Balance', ar: 'رصيد الكاش' },
      bankBalance: { en: 'Bank Balance', ar: 'رصيد البنك' },
      totalBalance: { en: 'Total Balance', ar: 'الرصيد الإجمالي' },
      cashIn: { en: 'Cash In', ar: 'الوارد نقداً' },
      cashOut: { en: 'Cash Out', ar: 'الصادر نقداً' },
      bankIn: { en: 'Bank In', ar: 'الوارد للبنك' },
      bankOut: { en: 'Bank Out', ar: 'الصادر من البنك' },
      recentTransactions: { en: 'Recent Transactions', ar: 'آخر المعاملات' },
      adjustments: { en: 'Balance Adjustments', ar: 'تعديلات الرصيد' },
      addAdjustment: { en: 'Add Adjustment', ar: 'إضافة تعديل' },
      deposit: { en: 'Deposit', ar: 'إيداع' },
      withdrawal: { en: 'Withdrawal', ar: 'سحب' },
      transfer: { en: 'Transfer', ar: 'تحويل' },
      cash: { en: 'Cash', ar: 'كاش' },
      bank: { en: 'Bank', ar: 'بنك' },
      amount: { en: 'Amount', ar: 'المبلغ' },
      description: { en: 'Description', ar: 'الوصف' },
      notes: { en: 'Notes', ar: 'ملاحظات' },
      type: { en: 'Type', ar: 'النوع' },
      account: { en: 'Account', ar: 'الحساب' },
      from: { en: 'From', ar: 'من' },
      to: { en: 'To', ar: 'إلى' },
      save: { en: 'Save', ar: 'حفظ' },
      cancel: { en: 'Cancel', ar: 'إلغاء' },
      date: { en: 'Date', ar: 'التاريخ' },
      direction: { en: 'Direction', ar: 'الاتجاه' },
      incoming: { en: 'Incoming', ar: 'وارد' },
      outgoing: { en: 'Outgoing', ar: 'صادر' },
      sale: { en: 'Sale', ar: 'مبيعات' },
      expense: { en: 'Expense', ar: 'مصروف' },
      purchase: { en: 'Purchase', ar: 'مشتريات' },
      salary: { en: 'Salary', ar: 'راتب' }
    };
    return translations[key]?.[language] || translations[key]?.en || key;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Wallet className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">{t('balanceSheet')}</h1>
            <p className="text-sm text-muted-foreground">
              {language === 'ar' ? 'متابعة الكاش ورصيد البنك' : 'Track cash and bank balances'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 me-2" />
            {language === 'ar' ? 'تحديث' : 'Refresh'}
          </Button>
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="h-4 w-4 me-2" />
            {t('addAdjustment')}
          </Button>
        </div>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Cash Balance */}
        <Card className="border-green-200 bg-gradient-to-br from-green-50 to-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t('cashBalance')}</p>
                <p className="text-3xl font-bold text-green-600">
                  {formatCurrency(balance?.cash_balance)}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <Wallet className="h-8 w-8 text-green-600" />
              </div>
            </div>
            <div className="mt-4 flex gap-4 text-sm">
              <div className="flex items-center gap-1 text-green-600">
                <ArrowUpRight className="h-4 w-4" />
                <span>{formatCurrency(balance?.cash_in)}</span>
              </div>
              <div className="flex items-center gap-1 text-red-500">
                <ArrowDownRight className="h-4 w-4" />
                <span>{formatCurrency(balance?.cash_out)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bank Balance */}
        <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t('bankBalance')}</p>
                <p className="text-3xl font-bold text-blue-600">
                  {formatCurrency(balance?.bank_balance)}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <Building2 className="h-8 w-8 text-blue-600" />
              </div>
            </div>
            <div className="mt-4 flex gap-4 text-sm">
              <div className="flex items-center gap-1 text-green-600">
                <ArrowUpRight className="h-4 w-4" />
                <span>{formatCurrency(balance?.bank_in)}</span>
              </div>
              <div className="flex items-center gap-1 text-red-500">
                <ArrowDownRight className="h-4 w-4" />
                <span>{formatCurrency(balance?.bank_out)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Total Balance */}
        <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t('totalBalance')}</p>
                <p className={`text-3xl font-bold ${balance?.total_balance >= 0 ? 'text-primary' : 'text-red-600'}`}>
                  {formatCurrency(balance?.total_balance)}
                </p>
              </div>
              <div className="p-3 bg-primary/10 rounded-full">
                {balance?.total_balance >= 0 ? (
                  <TrendingUp className="h-8 w-8 text-primary" />
                ) : (
                  <TrendingDown className="h-8 w-8 text-red-600" />
                )}
              </div>
            </div>
            <div className="mt-4 text-sm text-muted-foreground">
              {language === 'ar' ? 'الكاش + البنك' : 'Cash + Bank'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Transactions & Adjustments */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Transactions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              {t('recentTransactions')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {balance?.recent_transactions?.length > 0 ? (
              <div className="space-y-3">
                {balance.recent_transactions.map((tx, index) => (
                  <div 
                    key={`${tx.type}-${tx.date}-${index}`} 
                    className="flex items-center justify-between p-3 rounded-lg bg-secondary/30"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${
                        tx.direction === 'in' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-500'
                      }`}>
                        {tx.direction === 'in' ? (
                          <ArrowUpRight className="h-4 w-4" />
                        ) : (
                          <ArrowDownRight className="h-4 w-4" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{tx.description}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Badge variant="outline" className="text-xs">
                            {tx.payment_method === 'cash' ? t('cash') : t('bank')}
                          </Badge>
                          <span>{formatDate(tx.date)}</span>
                        </div>
                      </div>
                    </div>
                    <p className={`font-bold ${
                      tx.direction === 'in' ? 'text-green-600' : 'text-red-500'
                    }`}>
                      {tx.direction === 'in' ? '+' : '-'}{formatCurrency(tx.amount)}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">
                {language === 'ar' ? 'لا توجد معاملات' : 'No transactions'}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Balance Adjustments */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ArrowLeftRight className="h-5 w-5" />
              {t('adjustments')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {adjustments.length > 0 ? (
              <div className="space-y-3">
                {adjustments.slice(0, 10).map((adj) => (
                  <div 
                    key={adj.id} 
                    className="flex items-center justify-between p-3 rounded-lg bg-secondary/30"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${
                        adj.adjustment_type === 'deposit' ? 'bg-green-100 text-green-600' : 
                        adj.adjustment_type === 'withdrawal' ? 'bg-red-100 text-red-500' :
                        'bg-blue-100 text-blue-600'
                      }`}>
                        {adj.adjustment_type === 'transfer' ? (
                          <ArrowLeftRight className="h-4 w-4" />
                        ) : adj.adjustment_type === 'deposit' ? (
                          <ArrowUpRight className="h-4 w-4" />
                        ) : (
                          <ArrowDownRight className="h-4 w-4" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{adj.description}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Badge variant="outline" className="text-xs">
                            {t(adj.adjustment_type)}
                          </Badge>
                          {adj.adjustment_type === 'transfer' ? (
                            <span>{t(adj.from_account)} → {t(adj.to_account)}</span>
                          ) : (
                            <span>{t(adj.account)}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <p className="font-bold text-primary">
                      {formatCurrency(adj.amount)}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">
                {language === 'ar' ? 'لا توجد تعديلات' : 'No adjustments'}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add Adjustment Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('addAdjustment')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>{t('type')}</Label>
              <Select 
                value={formData.adjustment_type} 
                onValueChange={(value) => setFormData({ ...formData, adjustment_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="deposit">{t('deposit')}</SelectItem>
                  <SelectItem value="withdrawal">{t('withdrawal')}</SelectItem>
                  <SelectItem value="transfer">{t('transfer')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.adjustment_type === 'transfer' ? (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('from')}</Label>
                  <Select 
                    value={formData.from_account} 
                    onValueChange={(value) => setFormData({ ...formData, from_account: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">{t('cash')}</SelectItem>
                      <SelectItem value="bank">{t('bank')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('to')}</Label>
                  <Select 
                    value={formData.to_account} 
                    onValueChange={(value) => setFormData({ ...formData, to_account: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">{t('cash')}</SelectItem>
                      <SelectItem value="bank">{t('bank')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>{t('account')}</Label>
                <Select 
                  value={formData.account} 
                  onValueChange={(value) => setFormData({ ...formData, account: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">{t('cash')}</SelectItem>
                    <SelectItem value="bank">{t('bank')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>{t('amount')} (£)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>{t('description')}</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>{t('notes')}</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                {t('cancel')}
              </Button>
              <Button type="submit">{t('save')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
